<template>
    <!-- Show variations table only when in stock, or always when listing without stock -->
    <div v-if="(isInStock || listWithoutStock) && (!isBrochureMode || showVariants)" class="product-main__options table-wrapper">
        <table class="table product-option-table">
            <thead>
            <tr>
                <td class="product-option-table__label-main">Pot Size</td>
                <td class="product-option-table__label-price" v-if="showPrices">Price</td>
                <td class="product-option-table__label-qty" v-if="!isBrochureMode">Qty</td>
            </tr>
            </thead>
            <tbody>
            <tr v-for="variant in visibleVariants" :key="variant.id">
                <td class="product-option-table__row-name">
                    {{ variant.variant }}
                </td>
                <td class="product-option-table__row-price" v-if="showPrices">{{ listWithoutStock && Number(variant.price) <= 0 ? 'POA' : formatPrice(variant.price) }}</td>
                <td v-if="!isBrochureMode" class="quantity-control product-option-table__row-qty">
                    <div class="quantity-container">
                        <button type="button" class="number-decrease" @click="decreaseQuantity(variant)">-</button>
                        <input
                            type="number"
                            min="0"
                            :max="getMaxQuantity(variant)"
                            step="1"
                            v-model.number="variant.quantity"
                            @input="enforceQuantityLimit(variant)"
                        />
                        <button
                            type="button"
                            class="number-increase"
                            :disabled="isIncrementDisabled(variant)"
                            @click="increaseQuantity(variant)"
                        >+</button>
                    </div>
                </td>
            </tr>
            </tbody>
        </table>
    </div>

    <!-- Show button - either active "Add to Cart" or disabled with custom out of stock message -->
    <button
        v-if="!isBrochureMode"
        class="btn btn-primary btn-lg btn-block btn-rounded"
        :disabled="!isInStock"
        @click="isInStock ? applyCart() : null"
    >
        {{ isInStock ? (anyInCart ? 'Update Cart' : 'Add to Cart') : outOfStockMessage }}
    </button>

    <p
        v-if="!isBrochureMode && isInStock"
        class="product-main__cart-message"
        aria-live="polite"
    >
        {{ validationMessage || '\u00A0' }}
    </p>
</template>

<script setup>
import {reactive, ref, watch, computed, onMounted, onUnmounted} from 'vue'
import {useCart} from '@/frontend/vue/composables/useCart'

const props = defineProps({
    productName: String,
    productImage: String,
    productHandle: String,
    variants: {type: Array, required: true},
    isBrochureMode: {type: [Boolean, String], default: false},
    showVariants: {type: [Boolean, String], default: true},
    showPrices: {type: [Boolean, String], default: true},
    isInStock: {type: [Boolean, String], default: true},
    listWithoutStock: {type: [Boolean, String], default: false},
    outOfStockMessage: {type: String, default: 'Out of Stock'},
    defaultImage: {type: String, default: '/images/default.jpg'},
})

// Convert string props to boolean
const isBrochureMode = computed(() => {
    return props.isBrochureMode === true || props.isBrochureMode === 'true'
})

const showVariants = computed(() => {
    return props.showVariants === true || props.showVariants === 'true'
})

const showPrices = computed(() => {
    return props.showPrices === true || props.showPrices === 'true'
})

const isInStock = computed(() => {
    return props.isInStock === true || props.isInStock === 'true'
})

const listWithoutStock = computed(() => {
    return props.listWithoutStock === true || props.listWithoutStock === 'true'
})

// Access cart composable
const {cartItems, updateQuantity, formatPrice} = useCart()
const validationMessage = ref('')

// Create reactive local copy of variants with initial quantity from cart
// Variants are now pre-sorted by the backend using sort_order field
const variants = reactive(
    props.variants.map(v => {
        const line = cartItems.value.find(
            item => item.type === 'variant' && item.id === v.id
        )
        return {...v, quantity: line ? line.quantity : 0}
    })
)

// Sync quantities if cart changes externally
watch(
    () => cartItems.value,
    () => {
        variants.forEach(v => {
            const line = cartItems.value.find(
                item => item.type === 'variant' && item.id === v.id
            )
            v.quantity = line ? line.quantity : 0
        })
    },
    {deep: true}
)

function applyCart() {
    if (!hasSelectedQuantity.value && !anyInCart.value) {
        validationMessage.value = 'Select a quantity to add this product to your cart.'
        return
    }

    validationMessage.value = ''

    variants.forEach(v => {
        // updateQuantity will add, set, or remove based on quantity
        updateQuantity({
            id: v.id,
            type: 'variant',
            name: props.productName, // This will be passed from Blade with display name
            handle: props.productHandle,
            price: v.price,
            quantity: v.quantity,
            variant: v.variant,
            code: v.code,
            image: props.productImage ?? props.defaultImage,
            max_quantity: v.max_quantity, // Enforce quantity limits in cart
        })
    })
}

function decreaseQuantity(variant) {
    variant.quantity = Math.max(0, variant.quantity - 1)
    clearValidationMessage()
}

function increaseQuantity(variant) {
    const maxQty = getMaxQuantity(variant)
    if (variant.quantity < maxQty) {
        variant.quantity++
    }
    clearValidationMessage()
}

function enforceQuantityLimit(variant) {
    const maxQty = getMaxQuantity(variant)
    if (variant.quantity > maxQty) {
        variant.quantity = maxQty
    }
    if (variant.quantity < 0) {
        variant.quantity = 0
    }
    clearValidationMessage()
}

function clearValidationMessage() {
    validationMessage.value = ''
}

// Helper function to get max quantity (handles undefined/null and zero)
function getMaxQuantity(variant) {
    // If max_quantity is explicitly set (including 0), use it
    if (variant.max_quantity !== undefined && variant.max_quantity !== null) {
        return variant.max_quantity
    }
    // Default fallback for variants without max_quantity
    return 999
}

// Helper function to check if increment button should be disabled
function isIncrementDisabled(variant) {
    const maxQty = getMaxQuantity(variant)
    return variant.quantity >= maxQty
}

// Filter variants to only show those with available stock (max_quantity > 0), or all when listing without stock
const visibleVariants = computed(() => {
    if (listWithoutStock.value) {
        return variants
    }

    return variants.filter(v => {
        // If max_quantity is undefined/null, assume it's available (backward compatibility)
        if (v.max_quantity === undefined || v.max_quantity === null) {
            return true
        }
        // Only show variants with stock available
        return v.max_quantity > 0
    })
})

const anyInCart = computed(() =>
    variants.some(v =>
        cartItems.value.some(item => item.type === 'variant' && item.id === v.id)
    )
)

const any_qty = computed(() =>
    variants.some(v => v.quantity > 0)
)

const hasSelectedQuantity = computed(() =>
    visibleVariants.value.some(v => Number(v.quantity) > 0)
)

// Add event listener for apply-cart event
const handleApplyCart = () => {
    applyCart();
};

onMounted(() => {
    window.addEventListener('apply-cart', handleApplyCart);
});

onUnmounted(() => {
    window.removeEventListener('apply-cart', handleApplyCart);
});

</script>
<style>
.quantity-container {
    display: flex;
    align-items: center;
}
.quantity-container button, .quantity-container input {
    height: 30px;
    box-sizing: border-box;
}
</style>
